import type { IScope } from "angular";
import { finalize } from "rxjs/operators";

import "angular-translate";

import type { ICountry, ILanguage, IPartner, IPartnerInsert, IResponseWithLoading, IrMap } from "App/Modules/Models";
import type {
    ICountriesRepository,
    ILocalesRepository,
    IPartnersRepository,
    ITimeZonesRepository,
    ITranslationsRepository,
} from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class AddPartnerComponentController {
    public static $inject: string[] = [
        "$scope",
        "$translate",
        "mmPartnersRepository",
        "irLocalesRepository",
        "mmTranslationsRepository",
        "mmTimeZonesRepository",
        "irCountriesRepository",
    ];

    public partner: IPartnerInsert;

    public languages: IResponseWithLoading<ILanguage[]>;

    public defaultLanguage: ILanguage;

    public locales: IResponseWithLoading<string[]>;

    public timeZones: IResponseWithLoading<IrMap<string, string>>;

    public countries: IResponseWithLoading<ICountry[]>;

    public resolve: IFormContainerResolve<IPartner, void>;

    public addResponse: IResponseWithLoading<IPartner>;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly partnersRepository: IPartnersRepository,
        private readonly localesRepository: ILocalesRepository,
        private readonly translationsRepository: ITranslationsRepository,
        private readonly timeZonesRepository: ITimeZonesRepository,
        private readonly countriesRepository: ICountriesRepository,
    ) {}

    public $onInit() {
        extendWithLoadingAndFail(this.localesRepository.getAllLocales())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.locales = response;
                },
                (error) => {
                    this.locales = error;
                },
            );

        extendWithLoadingAndFail(this.translationsRepository.getLanguages())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.languages = response;

                    if (this.languages.data) {
                        // eslint-disable-next-line no-restricted-syntax
                        for (const language of this.languages.data) {
                            language.translated = this.$translate.instant(`Entities.Language.${language.key}`);
                        }
                    }
                },
                (error) => {
                    this.languages = error;
                },
            );

        extendWithLoadingAndFail(this.timeZonesRepository.getAllTimeZones())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<IrMap<string, string>>) => {
                    if (!response.isLoading && response.data) {
                        this.timeZones = response;
                    }
                },
                (error) => {
                    this.timeZones = error;
                },
            );

        extendWithLoadingAndFail(this.countriesRepository.getAllCountries())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<ICountry[]>) => {
                    if (!response.isLoading && response.data) {
                        this.countries = response;

                        // eslint-disable-next-line no-restricted-syntax
                        for (const country of this.countries.data) {
                            country.translated = this.$translate.instant(`Entities.Country.${country.alpha2}`);
                        }
                    }
                },
                (error) => {
                    this.countries = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public addPartner() {
        this.partner.defaultLanguageId = this.defaultLanguage.id;
        this.partner.defaultLanguageKey = this.defaultLanguage.key;

        extendWithLoadingAndFail(this.partnersRepository.addPartner(this.partner))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.addResponse = response;
                    if (!response.isLoading && response.data) {
                        this.resolve.options.onOk(response.data);
                    }
                },
                (error) => {
                    this.addResponse = error;
                },
            );
    }
}
